import { clsx } from "clsx"

import type {
  SolarFinancingTermsDto,
  SolarSungageFinancingTermsDto,
} from "~/api/dto/solar-proposal-pricing.dto"

import { sungageDeferredPaymentFootnote } from "../BatteryOnlyProposal/sections/financial-summary-card"
import { formatUsdExact } from "../BatteryOnlyProposal/view-helpers"
import { CardEyebrow } from "../ProposalPresentation/pricing/summary-card"

export const SUNGAGE_ESCALATOR_DPP = `Escalator`

/** "2.9%", as V1 states Sungage's yearly escalation. */
const escalationPercent = (percentage: number): string =>
  `${Number(percentage.toFixed(2))}%`

const monthly = (amount: number): string => `${formatUsdExact(amount)}/mo`

/** V1's Sunrise Escalator disclosure (`SungageLoanSummary`), from the server's terms. */
export const sungageEscalatorFootnote = (
  terms: Pick<SolarFinancingTermsDto, `apr` | `termYears`>,
  escalatorPercentage: number,
): string =>
  `The above assumes a ${terms.termYears}-year term${terms.apr == null ? `` : ` and ${terms.apr.toFixed(2)}% APR`}. Monthly payments escalate ${escalationPercent(escalatorPercentage)} every 12 months for the life of the loan; the interest rate remains fixed.`

/**
 * The qualification under a Sungage payment: BrightStar's deferred-payment
 * disclosure (VIS-719's, the payment re-amortizes to the maximum if the
 * Deferred Payment Portion isn't repaid), or Sunrise Escalator's yearly
 * escalation. Null for every other lender.
 */
export const solarSungagePaymentFootnote = (
  terms: SolarFinancingTermsDto | null | undefined,
): { text: string; testId: string } | null => {
  const sungage = terms?.sungage
  if (terms == null || sungage == null) return null
  // The shared disclosures read a loan's terms; Solar's TPO terms never apply.
  const loan = {
    productLabel: terms.productLabel,
    termYears: terms.termYears,
    apr: terms.apr,
  }
  return sungage.dppProduct === SUNGAGE_ESCALATOR_DPP
    ? {
        text: sungageEscalatorFootnote(loan, sungage.escalatorPercentage ?? 0),
        testId: `solar-sungage-escalator-footnote`,
      }
    : {
        text: sungageDeferredPaymentFootnote(loan, sungage.maxMonthlyPayment),
        testId: `solar-sungage-deferred-footnote`,
      }
}

type TermRow = { label: string; value: string; note?: string }

/** The program's payments as the server sent them; nothing is computed here. */
export const solarSungageTermRows = (
  termYears: number,
  sungage: SolarSungageFinancingTermsDto,
): TermRow[] =>
  sungage.dppProduct === SUNGAGE_ESCALATOR_DPP
    ? [
        {
          label: `Year 1 monthly payment`,
          value: monthly(sungage.initialMonthlyPayment),
        },
        {
          label: `Year ${termYears} monthly payment`,
          value: monthly(sungage.finalMonthlyPayment),
        },
        {
          label: `Yearly escalation`,
          value: escalationPercent(sungage.escalatorPercentage ?? 0),
        },
      ]
    : [
        {
          label: `Initial monthly payment`,
          value: monthly(sungage.initialMonthlyPayment),
          note:
            sungage.deferredPeriodMonths == null
              ? undefined
              : `Months 1–${sungage.deferredPeriodMonths}`,
        },
        {
          label: `Continuing monthly payment`,
          value: monthly(sungage.continuingMonthlyPayment),
          note:
            sungage.deferredPeriodMonths == null
              ? `Once the Deferred Payment Portion is repaid`
              : `From month ${sungage.deferredPeriodMonths + 1}, once the Deferred Payment Portion is repaid`,
        },
        {
          label: `Deferred Payment Portion`,
          value: formatUsdExact(sungage.deferredPaymentPortion),
          note:
            sungage.deferredPeriodMonths == null
              ? undefined
              : `Repay by month ${sungage.deferredPeriodMonths}`,
        },
      ]

/**
 * A Sungage loan's program terms under the ledger: BrightStar's initial and
 * continuing payments and its Deferred Payment Portion, or Sunrise
 * Escalator's first- and final-year payments and escalation. Like the payment,
 * terms of a non-current Quote stay on screen but never print.
 */
export function SolarSungageLoanTerms({
  lender,
  terms,
  printable,
}: {
  lender: string
  terms: SolarFinancingTermsDto & { sungage: SolarSungageFinancingTermsDto }
  printable: boolean
}): JSX.Element {
  const rows = solarSungageTermRows(terms.termYears, terms.sungage)
  return (
    <div
      data-testid="solar-sungage-terms"
      className={clsx(
        `mt-6 border-t border-white/25 pt-5`,
        !printable && `print:hidden`,
      )}
    >
      <CardEyebrow className="mb-2">
        {`${lender} ${terms.sungage.fundingGroupLabel} terms`}
      </CardEyebrow>
      <dl className="m-0">
        {rows.map((row) => (
          <div
            key={row.label}
            data-testid="solar-sungage-term"
            className="flex items-baseline justify-between gap-6 border-b border-white/15 py-2.5 last:border-b-0"
          >
            <dt className="text-[length:var(--step--1)] text-white/95">
              {row.label}
              {row.note ? (
                <span className="block text-[length:var(--step--2)] text-white/70">
                  {row.note}
                </span>
              ) : null}
            </dt>
            <dd className="font-display m-0 text-[length:var(--step-0)] font-semibold tracking-[-0.01em] whitespace-nowrap tabular-nums">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
